/**
 * Oath Power Document Service
 * API calls for power document operations
 * Routes to /oath/powerinquiry/powers/* endpoints
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Get documents for a power
 * @param {string} powerNo - The power number
 * @returns {Promise<{success: boolean, data: PowerDocumentResponse[], error?: string}>}
 */
export const getPowerDocuments = async (powerNo) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers/${powerNo}/documents`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 403) {
        return { success: false, error: 'You do not have permission to view documents.', data: [] };
      }
      if (response.status === 404) {
        return { success: true, data: [] };
      }
      if (response.status === 503) {
        return { success: false, error: 'Document service is temporarily unavailable. Please try again later.', data: [] };
      }

      return { success: false, error: errorData.message || 'Failed to get documents', data: [] };
    }

    const data = await response.json();
    const documents = Array.isArray(data) ? data : (data.data || data.result || data.documents || []);
    return { success: true, data: documents };
  } catch (error) {
    console.error('Get power documents error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Download a document page
 * @param {string} powerNo - The power number
 * @param {string} docName - Document name (used as document ID in URL)
 * @param {string} pageId - Page ID
 * @param {string} [fileName] - Optional filename for the downloaded file
 * @returns {Promise<{success: boolean, data?: Blob, error?: string}>}
 */
export const downloadDocumentPage = async (powerNo, docName, pageId, fileName) => {
  try {
    let url = `${BASE_URL}oath/powerinquiry/powers/${powerNo}/documents/${docName}/pages/${pageId}`;
    if (fileName) {
      url += `?fileName=${encodeURIComponent(fileName)}`;
    }
    const response = await authFetch(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Failed to download page' };
    }

    const blob = await response.blob();
    return { success: true, data: blob };
  } catch (error) {
    console.error('Download document page error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get the download URL for a document page (for direct linking/display)
 * TIFF files are converted to JPEG server-side for direct browser display
 * @param {string} powerNo - The power number
 * @param {string} docName - Document name (used as document ID in URL)
 * @param {string} pageId - Page ID
 * @param {string} [fileName] - Optional filename for the downloaded file
 * @returns {string} The download URL
 */
export const getDocumentPageUrl = (powerNo, docName, pageId, fileName) => {
  let url = `${BASE_URL}oath/powerinquiry/powers/${powerNo}/documents/${docName}/pages/${pageId}`;
  if (fileName) {
    url += `?fileName=${encodeURIComponent(fileName)}`;
  }
  return url;
};

/**
 * Get the URL for downloading a document as PDF
 * @param {string} powerNo - The power number
 * @param {string} docId - Document ID (the `name` field from the documents list)
 * @returns {string} The PDF download URL
 */
export const getDocumentPdfUrl = (powerNo, docId) => {
  return `${BASE_URL}oath/powerinquiry/powers/${powerNo}/documents/${docId}/pdf`;
};

/**
 * Download a document as PDF (authenticated)
 * @param {string} powerNo - The power number
 * @param {string} docId - Document ID (the `name` field from the documents list)
 * @returns {Promise<{success: boolean, data?: Blob, error?: string}>}
 */
export const downloadDocumentPdf = async (powerNo, docId) => {
  try {
    const url = getDocumentPdfUrl(powerNo, docId);
    const response = await authFetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: 'Document not found or access denied.' };
      }
      if (response.status === 503) {
        return { success: false, error: 'Document service is temporarily unavailable. Please try again later.' };
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Failed to download document' };
    }

    const blob = await response.blob();
    return { success: true, data: blob };
  } catch (error) {
    console.error('Download document PDF error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Upload documents for a power
 * @param {string} powerNo - The power number
 * @param {string} documentType - The document type (e.g., "Forfeitures", "Discharges")
 * @param {File[]} files - Array of files to upload
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const uploadPowerDocuments = async (powerNo, documentType, files) => {
  try {
    const formData = new FormData();
    formData.append('documentType', documentType);
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await authFetch(
      `${BASE_URL}oath/powerinquiry/powers/${powerNo}/documents`,
      {
        method: 'POST',
        body: formData,
        // Note: Don't set Content-Type header - browser will set it with boundary for multipart/form-data
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Failed to upload documents' };
    }

    const data = await response.json().catch(() => ({}));
    return { success: true, data };
  } catch (error) {
    console.error('Upload power documents error:', error);
    return { success: false, error: 'Network error' };
  }
};
